import React, { useState } from "react";
import { Play, CheckCircle, AlertTriangle, Info } from "lucide-react";
import keepAliveInstanceApi from "../../api/keepAliveInstanceApi";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Spinner } from "components/ui/spinner";

/**
 * KeepAliveInstance Component
 *
 * This component provides a "Keep Alive" button and modal for a single instance.
 * It handles the keep-alive process with proper error handling and user feedback.
 */
const KeepAliveInstance = ({
    instanceId,
    environment,
    eventId,
    onKeepAliveComplete,
    isCompleted
}) => {
    // State management
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [ticketId, setTicketId] = useState("");

    // Event handlers
    const handleOpenChange = (open) => {
        setShow(open);
        if (open) {
            setError(null);
            setResult(null);
        }
    };

    const handleClose = () => {
        setShow(false);
        setError(null);
        setResult(null);
    };

    const handleKeepAlive = async () => {
        // Validate ticket ID
        if (!ticketId.trim()) {
            setError("Please enter a ticket ID");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await keepAliveInstanceApi(
                eventId,
                environment,
                [instanceId],
                ticketId.trim()
            );

            if (response && response.message) {
                setResult(response);
                onKeepAliveComplete(instanceId, true, response.message);
            } else {
                const errorMsg = "Failed to keep instance alive. Please try again.";
                setError(errorMsg);
                onKeepAliveComplete(instanceId, false, errorMsg);
            }
        } catch (err) {
            let errorMsg = "An unexpected error occurred. Please try again.";

            if (err.message) {
                if (err.message.includes("already done") ||
                    err.message.includes("ExecutionAlreadyExists") ||
                    err.message.includes("already exists") ||
                    err.message.includes("Keep Alive already done")) {
                    errorMsg = "This instance is already in keep-alive mode for this event and ticket combination.";
                    // Treat this as a success case since it's already done
                    onKeepAliveComplete(instanceId, true, errorMsg);
                    return;
                } else if (err.message.includes("Access denied")) {
                    errorMsg = "Access denied to Step Function. Please check IAM permissions or contact the administrator.";
                } else if (err.message.includes("role not found")) {
                    errorMsg = "Cross-account role not found. Please contact the administrator.";
                } else if (err.message.includes("Invalid parameters")) {
                    errorMsg = "Invalid request parameters. Please check your input.";
                } else if (err.message.includes("Network error")) {
                    errorMsg = "Network error. Please check your connection and try again.";
                } else {
                    errorMsg = err.message;
                }
            }

            setError(errorMsg);
            onKeepAliveComplete(instanceId, false, errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Render completed state
    if (isCompleted) {
        return (
            <Button variant="success" size="sm" disabled>
                <CheckCircle />
                Keep Alive Done
            </Button>
        );
    }

    // Render active keep alive button
    return (
        <>
            <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleOpenChange(true)}
                disabled={loading}
            >
                {loading ? (
                    <>
                        <Spinner className="h-4 w-4" />
                        Processing...
                    </>
                ) : (
                    <>
                        <Play />
                        Keep Alive
                    </>
                )}
            </Button>

            <Dialog open={show} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Play className="h-4 w-4 text-primary" />
                            Keep Instance Alive
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">Instance ID</label>
                            <Input type="text" value={instanceId} readOnly className="bg-muted" />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">Environment</label>
                            <Input type="text" value={environment} readOnly className="bg-muted" />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">Event ID</label>
                            <Input type="text" value={eventId} readOnly className="bg-muted" />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">
                                Ops Ticket ID <span className="text-destructive">*</span>
                            </label>
                            <Input
                                type="text"
                                value={ticketId}
                                onChange={(e) => setTicketId(e.target.value)}
                                placeholder="Enter ticket ID (e.g., OPS-1234)"
                                required
                            />
                            <p className="text-xs text-muted-foreground">
                                Enter the ticket ID for tracking this keep-alive request
                            </p>
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div><strong>Error:</strong> {error}</div>
                            </div>
                        )}

                        {result && (
                            <div
                                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                                    result.alreadyExists
                                        ? "border-primary/30 bg-primary/10"
                                        : "border-success/40 bg-success/10 text-success"
                                }`}
                            >
                                {result.alreadyExists ? (
                                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                                ) : (
                                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                )}
                                <div>
                                    <strong>{result.alreadyExists ? "Info:" : "Success!"}</strong> {result.message}
                                    {result.executionArn && (
                                        <div className="mt-2">
                                            <small><strong>Execution ARN:</strong> {result.executionArn}</small>
                                        </div>
                                    )}
                                    {result.alreadyExists && (
                                        <div className="mt-2">
                                            <small className="text-muted-foreground">
                                                This instance is already in keep-alive mode for this event and ticket combination.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="secondary" onClick={handleClose}>Close</Button>
                        </DialogClose>
                        <Button onClick={handleKeepAlive} disabled={loading}>
                            {loading ? (
                                <>
                                    <Spinner className="h-4 w-4" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Play />
                                    Keep Alive
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default KeepAliveInstance;
